import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchHistoryPrescriptionSurgery } from '@/services/medicalHistoryService';
import { fetchPersonalData } from '@/services/personalService';
import { fetchAllDoctor } from '@/services/doctorService';
import { setDoctorsRedux } from '@/redux/actions/doctorActions';
import { getCurrentUserId } from '@/utils/getCurrentUserId';

/** Normaliza `radiografia.data` de la respuesta API y etiqueta cada fila con su paciente. */
const extractRadiografiaRows = (response, paciente) => {
  const data = response?.radiografia?.data;
  if (data == null) return [];
  const arr = Array.isArray(data) ? data : [data];
  return arr.map(row =>
    paciente ? { ...row, paciente } : { ...row },
  );
};

export const useReport = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('radiografía');
  const [patientData, setPatientData] = useState(null);
  const [patientsxUser, setPatientsxUser] = useState([]);
  const doctors = useSelector(state => state.doctors);
  const loadPersonalMutexRef = useRef(false);
  const skipNextActiveTabReloadRef = useRef(true);
  const patientsxUserRef = useRef([]);
  const doctor = doctors?.find(d => Number(d.codigo) === 2);

  const buildPayloadForPatient = codPaciente => ({
    num: 10,
    cod_paciente: codPaciente,
    cod_medico: null,
    ...(activeTab === 'ecografía' ? { tipo_de_orden: 40 } : {}),
  });

  const loadReportData = async currentPatients => {
    try {
      setError(null);
      const authData = JSON.parse(
        localStorage.getItem('auth_data') || 'null',
      );
      const mainCodPaciente = authData?.paciente?.codigo_de_paciente;

      if (!mainCodPaciente) {
        throw new Error('No se encontró código de paciente principal');
      }

      setPatientData(authData?.paciente);

      const patientsToList =
        currentPatients === undefined ? patientsxUserRef.current : currentPatients;

      const assocPromises = patientsToList.map(async p => {
        const res = await fetchHistoryPrescriptionSurgery(
          buildPayloadForPatient(p.codigo_de_paciente),
        );
        return extractRadiografiaRows(res, p);
      });

      const mainPromise = fetchHistoryPrescriptionSurgery(
        buildPayloadForPatient(mainCodPaciente),
      ).then(res => extractRadiografiaRows(res, authData?.paciente));

      const [mainRows, ...assocBuckets] = await Promise.all([
        mainPromise,
        ...assocPromises,
      ]);

      const assocFlat = assocBuckets.flat();
      const merged = assocFlat.concat(mainRows);

      merged.sort((a, b) =>
        String(b.fecha_de_registro || '').localeCompare(
          String(a.fecha_de_registro || ''),
        ),
      );

      setReportData({ radiografia: { data: merged } });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error cargando resultados');
      setReportData({ radiografia: { data: [] } });
    }
  };

  const loadPersonalByDoctor = async () => {
    if (loadPersonalMutexRef.current) return;
    loadPersonalMutexRef.current = true;
    setIsLoading(true);

    try {
      if (!doctor) {
        console.warn('No existe doctor con código 2');
        return;
      }

      if (!doctor?.numero_de_dni || !doctor?.codigo_personal) {
        return;
      }

      const userId = getCurrentUserId();
      if (!userId) throw new Error('No se pudo obtener el usuario logueado');

      const response = await fetchPersonalData({
        numero_de_dni: doctor.numero_de_dni,
      });

      const pacientesRaw = Array.isArray(response?.pacientes)
        ? response.pacientes
        : [];
      const pacientesAsociados = pacientesRaw.filter(p => {
        if (p?.user_id == null || p.user_id === '') return false;
        return Number(p.user_id) === Number(userId);
      });

      if (pacientesRaw.length > 0 && pacientesAsociados.length === 0) {
        console.warn(
          '[Report] Personal devolvió pacientes pero ninguno coincide con userId',
          {
            userId,
            sampleUserIds: pacientesRaw.slice(0, 3).map(p => p.user_id),
          },
        );
      }

      setPatientsxUser(pacientesAsociados);
      patientsxUserRef.current = pacientesAsociados;
      await loadReportData(pacientesAsociados);
    } catch (err) {
      console.error('Error en loadPersonalByDoctor (Report):', err);
    } finally {
      loadPersonalMutexRef.current = false;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (doctor) {
      loadPersonalByDoctor();
      return;
    }

    if (!doctors || doctors.length === 0) {
      fetchAllDoctor()
        .then(response => {
          const medicosActivosOrdenados =
            response?.medicos
              ?.filter(
                m => String(m.est_personal) === '1' && m.orden_externo !== null,
              )
              ?.sort((a, b) => {
                const ordenA = Number(a.orden_externo) || 0;
                const ordenB = Number(b.orden_externo) || 0;
                return ordenA - ordenB;
              }) || [];
          dispatch(setDoctorsRedux(medicosActivosOrdenados));
        })
        .catch(err => {
          console.error('Error cargando médicos:', err);
          setError(err.message || 'Error cargando médicos');
          setIsLoading(false);
        });
      return;
    }

    setIsLoading(false);
  }, [doctor, doctors]);

  useEffect(() => {
    if (skipNextActiveTabReloadRef.current) {
      skipNextActiveTabReloadRef.current = false;
      return;
    }
    if (!doctor) return;

    const run = async () => {
      setIsLoading(true);
      try {
        await loadReportData(patientsxUserRef.current);
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [activeTab]);

  return {
    isLoading,
    reportData,
    error,
    activeTab,
    setActiveTab,
    patientData,
  };
};
